import React from "react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { getSeriesColor } from "../utils/chartColors";
import { formatNumber } from "../../../../shared/utils/numberFormatter";

// Fallback palette when no series colors
const CHART_COLORS = [
  theme.accentChart || "#1d0c46",
  theme.primaryLight || "#4B57AA",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
];

export default function InsightsDataTable({
  series,
  metricName,
  average,
  allSeries,
  seriesColors,
  breakdownProperties,
  breakdownFormat,
  chartType,
  fullTotalsByMetric,
}) {
  const isTableChart = String(chartType || "").toLowerCase().trim() === "table";

  // Table chart + breakdown: use GroupedFlatTable (Mixpanel-style, one row per deepest combo, no tree)
  if (isTableChart && breakdownFormat && allSeries?.length > 0 && allSeries[0]?.breakdownValues?.length > 0) {
    return (
      <GroupedFlatTable
        allSeries={allSeries}
        seriesColors={seriesColors}
        breakdownProperties={breakdownProperties}
        fullTotalsByMetric={fullTotalsByMetric}
      />
    );
  }

  // Non-table chart: hierarchical breakdown mode (row-span for dimension columns)
  if (breakdownFormat && allSeries?.length > 0 && allSeries[0]?.breakdownValues?.length > 0) {
    return (
      <HierarchicalTable
        allSeries={allSeries}
        seriesColors={seriesColors}
        breakdownProperties={breakdownProperties}
      />
    );
  }
  // Multi-series mode (flat)
  if (allSeries && Array.isArray(allSeries) && allSeries.length > 0) {
    return <MultiSeriesTable allSeries={allSeries} seriesColors={seriesColors} />;
  }
  // Legacy single series mode
  if (!series || !Array.isArray(series) || series.length === 0) {
    return null;
  }
  return <SingleSeriesTable series={series} metricName={metricName} average={average} seriesColors={seriesColors} />;
}

function SingleSeriesTable({ series, metricName, average, seriesColors }) {
  const color = (seriesColors && seriesColors.length > 0) ? seriesColors[0] : (theme.accentChart || "#1d0c46");

  // Format date for display (e.g., "Aug 17", "Sep 9")
  const formatDateForTable = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      return `${month} ${day}`;
    } catch (e) {
      // Fallback: show last 5 chars if date parsing fails
      return dateStr.slice(-5);
    }
  };

  // Format date for header (e.g., "Aug 17" -> "Aug 17")
  const formatDateHeader = (dateStr) => {
    return formatDateForTable(dateStr);
  };

  const cellBorder = `1px solid ${theme.border || "#E5E7EB"}`;
  return (
    <div className="mt-4 overflow-hidden" style={{ borderTop: cellBorder }}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ fontFamily: "Raleway, sans-serif", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: theme.bgOverlay, borderBottom: cellBorder }}>
              <th className="text-left py-3 px-4" style={{ color: theme.textMuted, fontWeight: 600, borderRight: cellBorder }}>
                Metric
              </th>
              <th className="text-right py-3 px-4" style={{ color: theme.textMuted, fontWeight: 600, borderRight: cellBorder }}>
                Average
              </th>
              {series.map((item, index) => (
                <th
                  key={index}
                  className="text-right py-3 px-4 min-w-[80px]"
                  style={{ color: theme.textMuted, fontWeight: 600, borderRight: cellBorder }}
                >
                  {formatDateHeader(item.date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: cellBorder }}>
              <td className="py-3 px-4" style={{ color: theme.textPrimary, borderRight: cellBorder }}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: color }}
                  />
                  <span>{metricName || "Metric"}</span>
                </div>
              </td>
              <td className="text-right py-3 px-4" style={{ color: theme.textPrimary, fontWeight: 500, borderRight: cellBorder }}>
                {average != null ? formatNumber(average, 1) : "0"}
              </td>
              {series.map((item, index) => (
                <td
                  key={index}
                  className="text-right py-3 px-4"
                  style={{
                    color: theme.textPrimary,
                    backgroundColor: index % 2 === 0 ? "transparent" : theme.bgOverlay,
                    borderRight: cellBorder,
                  }}
                >
                  {item.value != null ? formatNumber(item.value) : "0"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MultiSeriesTable({ allSeries, seriesColors }) {
  // Get all unique dates across all series
  const dateSet = new Set();
  allSeries.forEach(series => {
    (series.data || []).forEach(point => {
      if (point.date) dateSet.add(point.date);
    });
  });
  const dates = Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));

  // Format date for display (e.g., "Aug 17", "Sep 9")
  const formatDateForTable = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      return `${month} ${day}`;
    } catch (e) {
      return dateStr.slice(-5);
    }
  };

  // Create a map for quick lookup: date -> { metricId -> value }
  const dateValueMap = new Map();
  dates.forEach(date => {
    dateValueMap.set(date, new Map());
  });

  allSeries.forEach((series, seriesIndex) => {
    (series.data || []).forEach(point => {
      if (point.date && dateValueMap.has(point.date)) {
        dateValueMap.get(point.date).set(series.metricId, point.value || 0);
      }
    });
  });

  const cellBorder = `1px solid ${theme.border || "#E5E7EB"}`;
  return (
    <div className="mt-4 overflow-hidden" style={{ borderTop: cellBorder }}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ fontFamily: "Raleway, sans-serif", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: theme.bgOverlay, borderBottom: cellBorder }}>
              <th className="text-left py-3 px-4" style={{ color: theme.textMuted, fontWeight: 600, borderRight: cellBorder }}>
                Metric
              </th>
              <th className="text-right py-3 px-4" style={{ color: theme.textMuted, fontWeight: 600, borderRight: cellBorder }}>
                Average
              </th>
              {dates.map((date, index) => (
                <th
                  key={index}
                  className="text-right py-3 px-4 min-w-[80px]"
                  style={{ color: theme.textMuted, fontWeight: 600, borderRight: cellBorder }}
                >
                  {formatDateForTable(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allSeries.map((series, seriesIndex) => {
              const avg = series.summary?.average ?? series.summary?.avg ?? 0;
              const color = getSeriesColor(seriesIndex, seriesColors || CHART_COLORS);
              
              return (
                <tr
                  key={`${series.metricId ?? 'series'}-${seriesIndex}`}
                  style={{ borderBottom: cellBorder }}
                >
                  <td className="py-3 px-4" style={{ color: theme.textPrimary, borderRight: cellBorder }}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: color }}
                      />
                      <span>{series.metricName || `Metric ${String.fromCharCode(65 + seriesIndex)}`}</span>
                    </div>
                  </td>
                  <td className="text-right py-3 px-4" style={{ color: theme.textPrimary, fontWeight: 500, borderRight: cellBorder }}>
                    {formatNumber(avg, 1)}
                  </td>
                  {dates.map((date, dateIndex) => {
                    const value = dateValueMap.get(date)?.get(series.metricId) || 0;
                    return (
                      <td
                        key={dateIndex}
                        className="text-right py-3 px-4"
                        style={{
                          color: theme.textPrimary,
                          backgroundColor: dateIndex % 2 === 0 ? "transparent" : theme.bgOverlay,
                          borderRight: cellBorder,
                        }}
                      >
                        {formatNumber(value)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Grouped flat table (Mixpanel-style) for chartType === "table".
 * Columns: [Breakdown Columns...] → [Metric Columns...]
 * One row per deepest breakdown combination; no tree nesting.
 * Uses rowSpan to merge identical consecutive breakdown values.
 * Overall row at bottom.
 */
function GroupedFlatTable({ allSeries, seriesColors, breakdownProperties, fullTotalsByMetric }) {
  const numDims = Math.max(
    ...allSeries.map((s) => (s.breakdownValues?.length ?? 0)),
    1
  );
  const dimLabels = breakdownProperties?.length
    ? breakdownProperties.slice(0, numDims).map((p) => p.propertyLabel || p.property || "Dimension")
    : Array.from({ length: numDims }, (_, i) => `Dim ${i + 1}`);

  // Unique metrics (by metricId)
  const metricMap = new Map();
  allSeries.forEach((s, idx) => {
    const id = s.metricId ?? s.metricName ?? `m${idx}`;
    if (!metricMap.has(id)) {
      metricMap.set(id, {
        metricId: id,
        metricName: s.fullMetricName ?? s.metricName ?? `Metric`,
        color: getSeriesColor(metricMap.size, seriesColors || CHART_COLORS),
      });
    }
  });
  const metrics = Array.from(metricMap.values());

  // Build lookup: breakdownKey -> { metricId -> total }
  const breakdownKey = (vals) =>
    (vals || []).map((v) => String(v ?? "")).join("\x00");
  const rowMap = new Map();
  allSeries.forEach((s) => {
    const vals = s.breakdownValues ?? (s.breakdownKey ? [s.breakdownKey] : []);
    const padded = [...vals];
    while (padded.length < numDims) padded.push("(none)");
    const key = breakdownKey(padded);
    if (!rowMap.has(key)) rowMap.set(key, { dimValues: padded, values: new Map() });
    const entry = rowMap.get(key);
    const mid = s.metricId ?? s.metricName ?? "m";
    const total = s.summary?.total ?? (s.data || []).reduce((sum, p) => sum + (p.value ?? 0), 0);
    entry.values.set(mid, total);
  });

  // Flat rows sorted by breakdown
  const rows = Array.from(rowMap.values())
    .sort((a, b) => {
      for (let d = 0; d < numDims; d++) {
        const va = String(a.dimValues[d] ?? "");
        const vb = String(b.dimValues[d] ?? "");
        const c = va.localeCompare(vb);
        if (c !== 0) return c;
      }
      return 0;
    });

  // Overall totals: use fullTotalsByMetric (API totals) when provided, else sum displayed rows
  const overallTotals = new Map();
  metrics.forEach((m) => {
    const fullTotal = fullTotalsByMetric && (fullTotalsByMetric[m.metricId] ?? fullTotalsByMetric[m.metricName]) != null
      ? fullTotalsByMetric[m.metricId] ?? fullTotalsByMetric[m.metricName]
      : null;
    overallTotals.set(m.metricId, fullTotal != null ? fullTotal : 0);
  });
  if (!fullTotalsByMetric) {
    rows.forEach((r) => {
      metrics.forEach((m) => {
        const v = r.values.get(m.metricId) ?? 0;
        overallTotals.set(m.metricId, (overallTotals.get(m.metricId) ?? 0) + v);
      });
    });
  }

  // Row-span for breakdown columns: merge consecutive rows with same value for dim (and all parent dims)
  const getRowSpan = (rowIdx, dimIdx) => {
    if (rowIdx >= rows.length) return 0;
    const row = rows[rowIdx];
    let span = 1;
    for (let r = rowIdx + 1; r < rows.length; r++) {
      const curr = rows[r];
      for (let d = 0; d <= dimIdx; d++) {
        if (String(curr.dimValues[d] ?? "") !== String(row.dimValues[d] ?? "")) return span;
      }
      span++;
    }
    return span;
  };

  const shouldRenderCell = (rowIdx, dimIdx) => {
    if (rowIdx === 0) return true;
    const row = rows[rowIdx];
    const prev = rows[rowIdx - 1];
    for (let d = 0; d <= dimIdx; d++) {
      if (String(row.dimValues[d] ?? "") !== String(prev.dimValues[d] ?? "")) return true;
    }
    return false;
  };

  const cellBorder = `1px solid ${theme.border || "#E5E7EB"}`;

  return (
    <div className="mt-4 overflow-hidden" style={{ borderTop: cellBorder }}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ fontFamily: "Raleway, sans-serif", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: theme.bgOverlay ?? "#F9FAFB", borderBottom: cellBorder }}>
              {dimLabels.map((label, i) => (
                <th
                  key={i}
                  className="text-left py-3 px-4"
                  style={{ color: theme.textMuted, fontWeight: 600, borderRight: cellBorder }}
                >
                  {label}
                </th>
              ))}
              {metrics.map((m, i) => (
                <th
                  key={m.metricId}
                  className="text-right py-3 px-4 min-w-[80px]"
                  style={{
                    color: theme.textMuted,
                    fontWeight: 600,
                    borderRight: i < metrics.length - 1 ? cellBorder : "none",
                    borderLeft: i === 0 ? `2px solid ${theme.border || "#E5E7EB"}` : undefined,
                  }}
                >
                  {m.metricName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} style={{ borderBottom: cellBorder }}>
                {dimLabels.map((_, dimIdx) =>
                  shouldRenderCell(rowIdx, dimIdx) ? (
                    <td
                      key={dimIdx}
                      rowSpan={getRowSpan(rowIdx, dimIdx)}
                      className="py-3 px-4"
                      style={{
                        color: theme.textPrimary,
                        verticalAlign: "top",
                        paddingTop: 12,
                        borderRight: cellBorder,
                      }}
                    >
                      {row.dimValues[dimIdx] ?? "(none)"}
                    </td>
                  ) : null
                )}
                {metrics.map((m, i) => {
                  const val = row.values.get(m.metricId);
                  const displayVal = val != null ? formatNumber(val) : "–";
                  return (
                    <td
                      key={m.metricId}
                      className="text-right py-3 px-4"
                      style={{
                        color: theme.textPrimary,
                        fontWeight: 500,
                        borderRight: i < metrics.length - 1 ? cellBorder : "none",
                        borderLeft: i === 0 ? `2px solid ${theme.border || "#E5E7EB"}` : undefined,
                      }}
                    >
                      {displayVal}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Overall row */}
            <tr style={{ borderBottom: cellBorder, backgroundColor: theme.bgOverlay ?? "#F9FAFB", fontWeight: 600 }}>
              <td
                colSpan={numDims}
                className="py-3 px-4"
                style={{
                  color: theme.textPrimary,
                  borderRight: cellBorder,
                }}
              >
                Overall
              </td>
              {metrics.map((m, i) => (
                <td
                  key={m.metricId}
                  className="text-right py-3 px-4"
                  style={{
                    color: theme.textPrimary,
                    borderRight: i < metrics.length - 1 ? cellBorder : "none",
                    borderLeft: i === 0 ? `2px solid ${theme.border || "#E5E7EB"}` : undefined,
                  }}
                >
                  {formatNumber(overallTotals.get(m.metricId) ?? 0)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Hierarchical table with row-span for identical consecutive dimension values.
 * Columns: Metrics | [Breakdown dims from breakdownProperties] | Average | [Date columns]
 * - Each metric block renders independently; rowSpan resets per metric.
 * - Within each metric: sort by breakdown hierarchy, then apply rowSpan on that block only.
 * - No cross-metric merging.
 */
function HierarchicalTable({ allSeries, seriesColors, breakdownProperties }) {
  const dateSet = new Set();
  allSeries.forEach((s) => {
    (s.data || []).forEach((p) => { if (p?.date) dateSet.add(p.date); });
  });
  const dates = Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return `${d.toLocaleDateString("en-US", { month: "short" })} ${d.getDate()}`;
    } catch (e) {
      return String(dateStr).slice(-5);
    }
  };

  const numDims = Math.max(
    ...allSeries.map((s) => (s.breakdownValues?.length ?? 0)),
    1
  );
  const dimLabels = breakdownProperties?.length
    ? breakdownProperties.slice(0, numDims).map((p) => p.propertyLabel || p.property || "Dimension")
    : Array.from({ length: numDims }, (_, i) => `Dim ${i + 1}`);

  // Raw row builder
  const toRow = (s, idx, color) => {
    const dimVals = s.breakdownValues ?? (s.breakdownKey ? [s.breakdownKey] : []);
    const padded = [...dimVals];
    while (padded.length < numDims) padded.push("(none)");
    const avg = s.summary?.average ?? s.summary?.avg ?? 0;
    const dataByDate = new Map((s.data || []).map((p) => [p.date, p.value ?? 0]));
    return {
      key: `${s.metricId ?? "m"}-${idx}`,
      metricId: s.metricId,
      metricName: s.fullMetricName ?? s.metricName,
      color,
      dimValues: padded,
      average: avg,
      dateValues: dates.map((d) => dataByDate.get(d) ?? 0),
    };
  };

  // Step 1: Group by metric (each metric = one block)
  const metricGroups = new Map();
  allSeries.forEach((s, idx) => {
    const mid = s.metricId ?? s.metricName ?? `m${idx}`;
    if (!metricGroups.has(mid)) {
      metricGroups.set(mid, {
        metricId: mid,
        metricName: s.fullMetricName ?? s.metricName ?? "Metric",
        color: getSeriesColor(metricGroups.size, seriesColors || CHART_COLORS),
        rows: [],
      });
    }
    metricGroups.get(mid).rows.push(toRow(s, idx, metricGroups.get(mid).color));
  });

  // Step 2: For each metric block, sort rows by breakdown hierarchy (Country, City, Browser...)
  const metricBlocks = Array.from(metricGroups.values()).map((block) => ({
    ...block,
    rows: block.rows.sort((a, b) => {
      for (let d = 0; d < numDims; d++) {
        const va = String(a.dimValues[d] ?? "");
        const vb = String(b.dimValues[d] ?? "");
        const c = va.localeCompare(vb);
        if (c !== 0) return c;
      }
      return 0;
    }),
  }));

  // Step 3: RowSpan logic PER METRIC BLOCK (reset for each block)
  const getRowSpanInBlock = (blockRows, localRowIdx, dimIdx) => {
    if (localRowIdx >= blockRows.length) return 0;
    const row = blockRows[localRowIdx];
    const val = dimIdx === 0 ? row.metricId : row.dimValues[dimIdx - 1];
    let span = 1;
    for (let r = localRowIdx + 1; r < blockRows.length; r++) {
      const curr = blockRows[r];
      for (let d = 0; d <= dimIdx; d++) {
        const cv = d === 0 ? curr.metricId : curr.dimValues[d - 1];
        const rv = d === 0 ? row.metricId : row.dimValues[d - 1];
        if (String(cv ?? "") !== String(rv ?? "")) return span;
      }
      span++;
    }
    return span;
  };

  const shouldRenderCellInBlock = (blockRows, localRowIdx, dimIdx) => {
    if (localRowIdx === 0) return true;
    const row = blockRows[localRowIdx];
    const prev = blockRows[localRowIdx - 1];
    for (let d = 0; d <= dimIdx; d++) {
      const v = d === 0 ? row.metricId : row.dimValues[d - 1];
      const pv = d === 0 ? prev.metricId : prev.dimValues[d - 1];
      if (String(v ?? "") !== String(pv ?? "")) return true;
    }
    return false;
  };

  const BORDER_TOP_GROUP = "2px solid #EEE";
  const BORDER_TOP_SUB = "1px solid #F5F5F5";
  const BORDER_COL = `1px solid ${theme.border || "#E5E7EB"}`;
  const METRIC_COL_WIDTH = 200;
  const DIM_COL_WIDTH = 120;

  return (
    <div className="mt-4" style={{ borderTop: `1px solid ${theme.border || "#E5E7EB"}` }}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ fontFamily: "Raleway, sans-serif", borderCollapse: "collapse" }}>
          <colgroup>
            <col style={{ minWidth: METRIC_COL_WIDTH }} />
            {dimLabels.map((_, i) => <col key={i} style={{ minWidth: DIM_COL_WIDTH }} />)}
            <col style={{ minWidth: 80 }} />
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: theme.bgOverlay ?? "#F9FAFB", borderBottom: BORDER_COL }}>
              <th className="text-left py-3 px-4" style={{ color: theme.textMuted, fontWeight: 600, borderRight: BORDER_COL }}>
                Metrics
              </th>
              {dimLabels.map((label, i) => (
                <th key={i} className="text-left py-3 px-4" style={{ color: theme.textMuted, fontWeight: 600, borderRight: BORDER_COL }}>
                  {label}
                </th>
              ))}
              <th className="text-right py-3 px-4" style={{ color: theme.textMuted, fontWeight: 600, borderRight: BORDER_COL }}>
                Average
              </th>
              {dates.map((d, i) => (
                <th key={i} className="text-right py-3 px-4 min-w-[80px]" style={{ color: theme.textMuted, fontWeight: 600, borderRight: BORDER_COL }}>
                  {formatDate(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metricBlocks.map((block, blockIdx) =>
              block.rows.map((row, localRowIdx) => (
                <tr
                  key={row.key}
                  style={{
                    borderTop: localRowIdx === 0 ? BORDER_TOP_GROUP : BORDER_TOP_SUB,
                  }}
                >
                  {/* Metric column: single cell per block spanning all rows */}
                  {localRowIdx === 0 && (
                    <td
                      rowSpan={block.rows.length}
                      className="py-3 px-4"
                      style={{
                        color: theme.textPrimary,
                        verticalAlign: "top",
                        paddingTop: 12,
                        borderRight: BORDER_COL,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: row.color }} />
                        <span>{row.metricName || "Metric"}</span>
                      </div>
                    </td>
                  )}
                  {/* Breakdown dimension columns: rowSpan within this metric block only */}
                  {dimLabels.map((_, dimIdx) =>
                    shouldRenderCellInBlock(block.rows, localRowIdx, dimIdx + 1) ? (
                      <td
                        key={dimIdx}
                        rowSpan={getRowSpanInBlock(block.rows, localRowIdx, dimIdx + 1)}
                        className="py-3 px-4"
                        style={{
                          color: theme.textPrimary,
                          verticalAlign: "top",
                          paddingTop: 12,
                          borderRight: BORDER_COL,
                        }}
                      >
                        {row.dimValues[dimIdx] ?? "(none)"}
                      </td>
                    ) : null
                  )}
                  {/* Average */}
                  <td className="text-right py-3 px-4" style={{ color: theme.textPrimary, fontWeight: 500, borderRight: BORDER_COL }}>
                    {formatNumber(row.average, 1)}
                  </td>
                  {/* Date columns */}
                  {row.dateValues.map((val, dateIdx) => (
                    <td
                      key={dateIdx}
                      className="text-right py-3 px-4"
                      style={{
                        color: theme.textPrimary,
                        backgroundColor: dateIdx % 2 === 0 ? "transparent" : (theme.bgOverlay ?? "#F9FAFB"),
                        borderRight: BORDER_COL,
                      }}
                    >
                      {formatNumber(val)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
